import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Clock, Check, Lock, ChevronRight, BarChart2, User, Calendar, Tv, MapPin, Star, Users, Award, TrendingUp, Settings, RefreshCw, Flame, Target, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import SocialLoginButtons from '../components/SocialLoginButtons';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Streak Badge Component with animations
const StreakBadge = ({ streak, showCelebration }) => {
  const getBadgeConfig = (streak) => {
    if (streak >= 10) return { 
      icon: '🏆', 
      label: 'LEGENDARY', 
      color: 'from-yellow-400 via-amber-500 to-orange-600',
      textColor: 'text-yellow-900',
      glow: 'shadow-[0_0_30px_rgba(251,191,36,0.6)]',
      animate: 'animate-pulse'
    };
    if (streak >= 5) return { 
      icon: '🔥', 
      label: 'ON FIRE', 
      color: 'from-orange-500 via-red-500 to-pink-600',
      textColor: 'text-white',
      glow: 'shadow-[0_0_20px_rgba(239,68,68,0.5)]',
      animate: 'animate-bounce'
    };
    if (streak >= 3) return { 
      icon: '⚡', 
      label: 'HOT STREAK', 
      color: 'from-blue-500 via-purple-500 to-pink-500',
      textColor: 'text-white',
      glow: 'shadow-[0_0_15px_rgba(139,92,246,0.4)]',
      animate: ''
    };
    return null;
  };

  const badge = getBadgeConfig(streak);
  if (!badge) return null;

  return (
    <div className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${badge.color} ${badge.glow} ${badge.animate} transition-all duration-300`}>
      <span className="text-2xl">{badge.icon}</span>
      <div className="flex flex-col">
        <span className={`text-xs font-black uppercase tracking-wider ${badge.textColor}`}>{badge.label}</span>
        <span className={`text-lg font-bold ${badge.textColor}`}>{streak} in a row!</span>
      </div>
      {showCelebration && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
      )}
    </div>
  );
};

// Celebration function - with safety check
const triggerCelebration = (streak) => {
  if (typeof confetti !== 'function') return; // Safety check
  
  try {
    const particleCount = streak >= 10 ? 200 : streak >= 5 ? 100 : 50;
    
    // Fire confetti from both sides
    const colors = streak >= 10 
      ? ['#FFD700', '#FFA500', '#FF6347', '#FFE4B5']
      : streak >= 5 
      ? ['#FF4500', '#FF6347', '#FF7F50', '#FFD700']
      : ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981'];

    confetti({
      particleCount,
      spread: 70,
      origin: { x: 0.1, y: 0.6 },
      colors,
      startVelocity: 45,
    });
    
    confetti({
      particleCount,
      spread: 70,
      origin: { x: 0.9, y: 0.6 },
      colors,
      startVelocity: 45,
    });

    // For legendary (10+), add extra celebration
    if (streak >= 10) {
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 100,
          origin: { x: 0.5, y: 0.3 },
          colors,
          startVelocity: 55,
        });
      }, 500);
    }
  } catch (e) {
    console.warn('Confetti error:', e);
  }
};

// Admin Panel Component - directly on Pick'em page
const AdminPanel = ({ onRefresh }) => {
  const [showAdmin, setShowAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);

  const loadDraftStatus = async (adminPassword = password) => {
    const res = await axios.get(`${API}/pickem/admin/draft-status`, { params: { password: adminPassword } });
    setDraftStatus(res.data);
    return res.data;
  };
  
  const handleAuth = async () => {
    try {
      const res = await axios.get(`${API}/pickem/admin/analytics`, { params: { password } });
      if (res.data) {
        setAuthenticated(true);
        await loadDraftStatus(password);
        toast.success('Admin access granted');
      }
    } catch (e) {
      toast.error('Invalid password');
    }
  };

  const activatePreparedWeek = async () => {
    const weekNumber = draftStatus?.prepared_draft?.number;
    if (!weekNumber) {
      toast.error('No prepared draft is available to activate');
      return;
    }
    if (!window.confirm(`Activate prepared Week ${weekNumber}? The current public week will remain unchanged if validation fails.`)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pickem/admin/rebuild-prepared-week`, null, { params: { password } });
      toast.success(res.data.message);
      await loadDraftStatus(password);
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const problems = typeof detail === 'object' ? detail.errors?.join(' ') : detail;
      toast.error(problems || 'Activation failed; the active week is unchanged.');
      await loadDraftStatus(password);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreparedDraft = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/pickem/admin/prepare-next-week`, null, { params: { password } });
      await loadDraftStatus(password);
      toast.success('Prepared draft refreshed from the scoreboard');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const problems = typeof detail === 'object' ? detail.errors?.join(' ') : detail;
      toast.error(problems || 'Draft refresh needs attention');
      await loadDraftStatus(password);
    } finally {
      setLoading(false);
    }
  };
  
  const rebuildWeek0 = async () => {
    if (!window.confirm('Rebuild Week 0? This will create/update all Week 0 games. Existing picks are preserved.')) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pickem/admin/rebuild-week0`, null, { params: { password } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebuild Week 0');
    } finally {
      setLoading(false);
    }
  };
  
  const rebuildWeek1 = async () => {
    if (!window.confirm('Rebuild Week 1? This will create/update all Week 1 games and set it as active. Existing picks are preserved.')) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pickem/admin/rebuild-week1`, null, { params: { password } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebuild Week 1');
    } finally {
      setLoading(false);
    }
  };
  
  const rebuildWeek2 = async () => {
    if (!window.confirm('Rebuild Week 2? This will create 10 new games. Week 0 & Week 1 picks are fully preserved.')) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pickem/admin/rebuild-week2`, null, { params: { password } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebuild Week 2');
    } finally {
      setLoading(false);
    }
  };

  const rebuildWeek3 = async () => {
    if (!window.confirm('Rebuild Week 3? This will create 9 new games. All previous picks are fully preserved.')) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pickem/admin/rebuild-week3`, null, { params: { password } });
      toast.success(res.data.message);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebuild Week 3');
    } finally {
      setLoading(false);
    }
  };

  const [backupData, setBackupData] = useState(null);
  const [showBackup, setShowBackup] = useState(false);

  const runBackup = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pickem/admin/backup-picks`, { params: { password } });
      setBackupData(res.data);
      setShowBackup(true);
      toast.success(`Backup complete! ${res.data.total_picks} picks from ${res.data.unique_users} users`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to backup picks');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackup = () => {
    if (!backupData) return;
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pickem-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    toast.success('Backup downloaded!');
  };
  
  if (!showAdmin) {
    return (
      <button
        onClick={() => setShowAdmin(true)}
        className="fixed bottom-4 right-4 bg-slate-800 text-white p-3 rounded-full shadow-lg hover:bg-slate-700 z-50"
        title="Admin Panel"
        data-testid="admin-toggle-btn"
      >
        <Settings className="h-5 w-5" />
      </button>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-xl shadow-2xl p-4 z-50 w-80 border border-slate-200" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <Settings className="h-4 w-4" /> Admin Panel
        </h3>
        <button onClick={() => setShowAdmin(false)} className="text-slate-400 hover:text-slate-600">✕</button>
      </div>
      
      {!authenticated ? (
        <div className="space-y-2">
          <input
            type="password"
            placeholder="Admin Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
            data-testid="admin-panel-password"
          />
          <Button onClick={handleAuth} className="w-full bg-slate-800 text-white" data-testid="admin-panel-login">
            Login
          </Button>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {/* BACKUP SECTION - Always visible at top */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-2">
              <Award className="h-4 w-4" />
              🔒 Backup Picks First!
            </div>
            <Button
              onClick={runBackup}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mb-2"
              data-testid="backup-picks-btn"
            >
              <Users className={`h-4 w-4 mr-2`} />
              {loading ? 'Backing up...' : 'Backup All Picks'}
            </Button>
            
            {backupData && (
              <div className="text-xs text-emerald-700 space-y-1">
                <div className="font-bold">✅ Backup Complete!</div>
                <div>Total Picks: <span className="font-semibold">{backupData.total_picks}</span></div>
                <div>Unique Users: <span className="font-semibold">{backupData.unique_users}</span></div>
                <div>By Week:</div>
                <ul className="ml-3">
                  {Object.entries(backupData.picks_by_week || {}).map(([week, count]) => (
                    <li key={week}>Week {week}: <span className="font-semibold">{count}</span> picks</li>
                  ))}
                </ul>
                <Button
                  onClick={downloadBackup}
                  size="sm"
                  className="w-full mt-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
                  data-testid="download-backup-btn"
                >
                  📥 Download Backup JSON
                </Button>
              </div>
            )}
          </div>

          <div className="border-t pt-3 text-xs text-slate-500" data-testid="prepared-week-status">
            <div className="font-bold text-slate-700">Active week: {draftStatus?.active_week?.number ?? '—'} · {draftStatus?.active_week?.game_count ?? 0} games</div>
            {draftStatus?.prepared_draft ? (
              <div className="mt-2 space-y-1">
                <div className="font-bold text-blue-700">Prepared draft: Week {draftStatus.prepared_draft.number} · {draftStatus.prepared_draft.game_count} games</div>
                <div>Last background sync: {draftStatus.last_successful_background_sync ? new Date(draftStatus.last_successful_background_sync).toLocaleString() : 'Not yet completed'}</div>
                {draftStatus.last_sync_error && (
                  <div className="rounded bg-amber-50 p-2 text-amber-800" data-testid="prepared-week-sync-warning">
                    Automatic recovery is retrying: {draftStatus.last_sync_error}
                  </div>
                )}
                {draftStatus.prepared_draft.validation_errors?.length > 0 && (
                  <div className="rounded bg-red-50 p-2 text-red-700" data-testid="prepared-week-validation-errors">
                    {draftStatus.prepared_draft.validation_errors.map((error, index) => <div key={index}>{error}</div>)}
                  </div>
                )}
                <Button onClick={refreshPreparedDraft} disabled={loading} variant="outline" className="mt-2 w-full" data-testid="refresh-prepared-week-button">
                  <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Prepared Draft
                </Button>
                <Button onClick={activatePreparedWeek} disabled={loading || draftStatus.prepared_draft.validation_errors?.length > 0} className="mt-2 w-full bg-[#00204E] text-white hover:bg-[#003366]" data-testid="rebuild-activate-prepared-week-button">
                  {loading ? 'Working...' : `Rebuild Week — Activate Week ${draftStatus.prepared_draft.number}`}
                </Button>
              </div>
            ) : (
              <div className="mt-2 text-amber-700" data-testid="prepared-week-missing-info">No prepared draft is ready yet.</div>
            )}
          </div>
          
          {/* PICK RECOVERY SECTION */}
          <div className="border-t pt-2 mt-2 text-xs text-slate-500 mb-2">🚨 Pick Recovery</div>
          <Button
            onClick={async () => {
              setLoading(true);
              try {
                const res = await axios.get(`${API}/pickem/admin/orphaned-picks`, { params: { password } });
                const data = res.data;
                if (data.orphaned_picks === 0) {
                  toast.success(`✅ No orphaned picks found! All ${data.valid_picks} picks are valid.`);
                } else {
                  toast.warning(`⚠️ Found ${data.orphaned_picks} orphaned picks out of ${data.total_picks} total`);
                  console.log('Orphaned picks:', data.orphaned_details);
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to check picks');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
            data-testid="check-orphaned-btn"
          >
            🔍 {loading ? 'Checking...' : 'Check Orphaned Picks'}
          </Button>
          <Button
            onClick={async () => {
              if (!window.confirm('🚨 RECOVER ORPHANED PICKS?\n\nThis will attempt to re-link any orphaned picks to the correct games.\n\nClick OK to proceed.')) return;
              setLoading(true);
              try {
                const res = await axios.post(`${API}/pickem/admin/recover-orphaned-picks`, null, { params: { password } });
                const data = res.data;
                toast.success(`✅ Recovery complete! ${data.recovered} picks recovered, ${data.failed} could not be recovered.`);
                console.log('Recovery details:', data.details);
                onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to recover picks');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            data-testid="recover-picks-btn"
          >
            🔧 {loading ? 'Recovering...' : 'Recover Orphaned Picks'}
          </Button>
          
          <div className="border-t pt-2 mt-2 text-xs text-slate-500 mb-2">Week 5 Fix</div>
          <Button
            onClick={async () => {
              if (!window.confirm('Add Texas Southern @ Florida Atlantic to Week 5?')) return;
              setLoading(true);
              try {
                const res = await axios.post(`${API}/pickem/admin/add-tsu-fau-game`, null, { params: { password } });
                toast.success(res.data.message);
                onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to add game');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold"
            data-testid="add-tsu-fau-btn"
          >
            🏈 {loading ? 'Adding...' : 'Add TSU @ FAU Game'}
          </Button>
          
          <div className="border-t pt-2 mt-2 text-xs text-slate-500 mb-2">Score Entry</div>
          <Button
            onClick={async () => {
              if (!window.confirm('Enter Week 1 scores? This will update standings for all participants.')) return;
              setLoading(true);
              try {
                const res = await axios.post(`${API}/pickem/admin/enter-week1-scores`, null, { params: { password } });
                toast.success(res.data.message);
                onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to enter scores');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="enter-week1-scores-btn"
          >
            <Trophy className={`h-4 w-4 mr-2`} />
            {loading ? 'Working...' : 'Enter Week 1 Scores (Update Standings)'}
          </Button>
          <Button
            onClick={async () => {
              if (!window.confirm('Fix Kentucky State? This changes Langston to Kentucky State and preserves all picks.')) return;
              setLoading(true);
              try {
                const res = await axios.post(`${API}/pickem/admin/fix-kentucky-state`, null, { params: { password } });
                toast.success(res.data.message);
                onRefresh();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to fix Kentucky State');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="fix-kentucky-state-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Working...' : 'Fix Kentucky State (Replace Langston)'}
          </Button>
          <p className="text-xs text-slate-500 mt-2">
            <strong>Enter Week 1 Scores</strong> preserves all picks and updates standings.<br/>
            <strong>Fix Kentucky State</strong> swaps Langston to Kentucky State with correct logo.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper to format kickoff time to EST timezone consistently
// Times are stored as Eastern Time (no Z suffix means local/Eastern, not UTC)
const formatKickoffTime = (isoString) => {
  if (!isoString) return 'TBD';
  
  // If the time doesn't have timezone info, treat it as Eastern Time
  // Don't let JavaScript interpret it as UTC
  let dateStr = isoString;
  if (!isoString.includes('Z') && !isoString.includes('+') && !isoString.includes('-', 10)) {
    // Append Eastern Time offset (EDT = -04:00, EST = -05:00)
    // Use EDT for October games
    dateStr = isoString + '-04:00';
  }
  
  const date = new Date(dateStr);
  
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short'
  });
};

// Helper to get countdown to first game
const getCountdownToFirstGame = (games) => {
  if (!games || games.length === 0) return null;
  
  const now = new Date();
  const upcomingGames = games.filter(g => !g.is_locked);
  
  if (upcomingGames.length === 0) return null;
  
  const firstGame = upcomingGames[0];
  const kickoff = new Date(firstGame.kickoff_time);
  const diff = kickoff - now;
  
  if (diff <= 0) return null;
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { days, hours, minutes, game: firstGame };
};

// Matchup Card Component
const MatchupCard = ({ game, userPick, pendingPick, onSelectPick, consensus, shelovestheePick, isLoggedIn }) => {
  const isLocked = game.is_locked;
  const badges = game.badges || [];
  const isFeatured = game.is_game_of_week || badges.includes('GOTW');
  // Check both saved picks and pending (unsaved) picks
  const selectedTeamId = pendingPick || userPick?.selected_team_id;
  const userPickedAway = selectedTeamId === game.away_team?.id;
  const userPickedHome = selectedTeamId === game.home_team?.id;
  const shelovestheePickedAway = shelovestheePick === game.away_team?.id;
  const shelovestheePickedHome = shelovestheePick === game.home_team?.id;
  const isPending = pendingPick && pendingPick !== userPick?.selected_team_id;
  
  const handlePickClick = (teamId) => {
    if (isLocked) return;
    // Call onSelectPick to set pending pick (doesn't save yet)
    onSelectPick(game.id, teamId);
  };
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 ${
        isFeatured ? 'border-yellow-400 ring-2 ring-yellow-300 shadow-xl' :
        game.is_conference_game ? 'border-purple-400' :
        isPending ? 'border-[#00204E]' : 'border-transparent'
      }`}
      data-testid={`matchup-${game.id}`}
    >
      {/* Game Info Header */}
      <div className={`px-4 py-2 flex items-center justify-between ${
        isFeatured ? 'bg-yellow-50' :
        game.is_conference_game ? 'bg-purple-50' :
        isPending ? 'bg-[#00204E]/10' : 'bg-slate-100'
      }`}>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="h-4 w-4" />
          <span>{formatKickoffTime(game.kickoff_time)}</span>
          {game.is_conference_game && (
            <span className="bg-purple-700 text-white text-[10px] font-black px-2 py-1 rounded-sm ml-1" data-testid={`conference-game-badge-${game.id}`}>
              SWAC CONFERENCE GAME
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isPending && (
            <span className="bg-[#00204E] text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
              UNSAVED
            </span>
          )}
          {isFeatured && (
            <span className="flex items-center gap-1 bg-yellow-400 text-slate-950 px-2 py-1 text-xs font-black rounded-sm" data-testid={`gotw-badge-${game.id}`}>
              <Star className="h-4 w-4 fill-current" /> GOTW
            </span>
          )}
          <span className="flex items-center gap-1 text-slate-500 text-xs">
            <Tv className="h-3 w-3" /> {game.network}
          </span>
        </div>
      </div>
      
      {/* Teams */}
      <div className="p-4">
        {badges.filter(badge => badge !== 'GOTW').length > 0 && (
          <div className="mb-3 flex flex-wrap justify-center gap-2">
            {badges.filter(badge => badge !== 'GOTW').map(badge => (
              <span key={badge} className="rounded-sm bg-rose-100 px-2 py-1 text-[10px] font-black tracking-wide text-rose-800" data-testid={`game-badge-${game.id}-${badge.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {badge}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-4">
          {/* Away Team */}
          <button
            onClick={() => handlePickClick(game.away_team.id)}
            disabled={isLocked}
            className={`flex-1 flex flex-col items-center p-4 rounded-lg transition-all ${
              userPickedAway 
                ? 'bg-[#00204E] border-2 border-[#00204E] text-white' 
                : isLocked 
                  ? 'bg-slate-50 cursor-not-allowed' 
                  : 'hover:bg-slate-100 cursor-pointer border-2 border-slate-200 hover:border-[#00204E]'
            }`}
            data-testid={`pick-away-${game.id}`}
          >
            <img 
              src={game.away_team?.logo_url} 
              alt={game.away_team?.name}
              className="h-16 w-16 object-contain mb-2"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=' + game.away_team?.abbreviation; }}
            />
            <span className={`font-bold text-center ${userPickedAway ? 'text-white' : 'text-slate-800'}`}>{game.away_team?.name}</span>
            <span className={`text-xs ${userPickedAway ? 'text-white/70' : 'text-slate-500'}`}>{game.away_team?.nickname}</span>
            {userPickedAway && (
              <span className="mt-2 flex items-center gap-1 text-white font-bold text-sm">
                <Check className="h-4 w-4" /> {isPending ? 'SELECTED' : 'YOUR PICK'}
              </span>
            )}
            {shelovestheePickedAway && !userPickedAway && (
              <span className="mt-1 text-yellow-600 text-xs font-bold">👑 shelovesthee</span>
            )}
          </button>
          
          {/* VS */}
          <div className="flex flex-col items-center">
            <span className="text-2xl font-bold text-slate-300">VS</span>
            {isLocked && (
              <Lock className="h-5 w-5 text-red-500 mt-1" />
            )}
          </div>
          
          {/* Home Team */}
          <button
            onClick={() => handlePickClick(game.home_team.id)}
            disabled={isLocked}
            className={`flex-1 flex flex-col items-center p-4 rounded-lg transition-all ${
              userPickedHome 
                ? 'bg-[#00204E] border-2 border-[#00204E] text-white' 
                : isLocked 
                  ? 'bg-slate-50 cursor-not-allowed' 
                  : 'hover:bg-slate-100 cursor-pointer border-2 border-slate-200 hover:border-[#00204E]'
            }`}
            data-testid={`pick-home-${game.id}`}
          >
            <img 
              src={game.home_team?.logo_url} 
              alt={game.home_team?.name}
              className="h-16 w-16 object-contain mb-2"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=' + game.home_team?.abbreviation; }}
            />
            <span className={`font-bold text-center ${userPickedHome ? 'text-white' : 'text-slate-800'}`}>{game.home_team?.name}</span>
            <span className={`text-xs ${userPickedHome ? 'text-white/70' : 'text-slate-500'}`}>{game.home_team?.nickname}</span>
            {userPickedHome && (
              <span className="mt-2 flex items-center gap-1 text-white font-bold text-sm">
                <Check className="h-4 w-4" /> {isPending ? 'SELECTED' : 'YOUR PICK'}
              </span>
            )}
            {shelovestheePickedHome && !userPickedHome && (
              <span className="mt-1 text-yellow-600 text-xs font-bold">👑 shelovesthee</span>
            )}
          </button>
        </div>
        
        {/* Consensus (only shows if user has picked) */}
        {consensus && userPick && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Audience Consensus</span>
              <span className="text-slate-400">{consensus.total_picks} picks</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs font-bold w-8">{consensus.away_team_percentage}%</span>
              <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${consensus.away_team_percentage}%` }}
                />
              </div>
              <span className="text-xs font-bold w-8 text-right">{consensus.home_team_percentage}%</span>
            </div>
          </div>
        )}
        
        {/* Location */}
        {game.venue && (
          <div className="mt-3 flex items-center justify-center gap-1 text-xs text-slate-400">
            <MapPin className="h-3 w-3" />
            <span>{game.venue}, {game.city}</span>
          </div>
        )}
        
        {/* Game Label */}
        {game.game_label && (
          <div className="mt-2 text-center">
            <span className="text-xs font-bold text-blue-600 uppercase">{game.game_label}</span>
          </div>
        )}

        {game.linked_article_id && (
          <div className="mt-3 text-center">
            <Link
              to={`/interactive/${game.linked_article_id}`}
              className="inline-flex items-center gap-1 text-sm font-bold text-[#DC2626] hover:underline"
              data-testid={`pickem-article-link-${game.id}`}
            >
              Read: {game.linked_article_title || 'Game preview'} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Leaderboard Component
const Leaderboard = ({ data, currentUserId, title = "THEE LEADERBOARD" }) => {
  const getRankEmoji = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };
  
  const getStreakBadge = (streak) => {
    if (streak >= 10) return { icon: '🏆', label: 'Legendary' };
    if (streak >= 5) return { icon: '🔥', label: 'On Fire' };
    if (streak >= 3) return { icon: '⚡', label: 'Hot' };
    return null;
  };
  
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="leaderboard">
      <div className="bg-gradient-to-r from-[#00204E] to-[#003366] px-4 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-400" />
          {title}
        </h3>
        <span className="text-white/70 text-sm">{data.length} participants</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
        {data.length === 0 ? (
          <div className="p-4 text-center text-slate-500">No picks yet. Be the first!</div>
        ) : (
          data.map((entry) => {
            const streakBadge = getStreakBadge(entry.current_streak);
            return (
              <div 
                key={entry.user_id}
                className={`flex items-center justify-between p-3 ${
                  entry.user_id === currentUserId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                } hover:bg-slate-50 transition-colors`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg w-10">{getRankEmoji(entry.rank)}</span>
                  <span className="font-semibold text-slate-800">{entry.nickname}</span>
                  {streakBadge && (
                    <span 
                      className="text-sm" 
                      title={`${streakBadge.label} - ${entry.current_streak} in a row!`}
                    >
                      {streakBadge.icon}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">{entry.correct_picks}-{entry.total_picks - entry.correct_picks}</span>
                  <span className="font-bold text-blue-600">{entry.correct_picks} pts</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      {data.length > 10 && (
        <div className="px-4 py-2 bg-slate-50 text-center text-xs text-slate-500 border-t">
          Scroll to see all {data.length} participants
        </div>
      )}
    </div>
  );
};

// Main Pick&apos;em Page
const SWACPickemPage = () => {
  // Use the app's existing auth context
  const { user } = useContext(AuthContext);
  
  const [games, setGames] = useState([]);
  const [allWeeks, setAllWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [userPicks, setUserPicks] = useState({});
  const [pendingPicks, setPendingPicks] = useState({}); // Unsaved picks
  const [consensus, setConsensus] = useState({});
  const [shelovestheePicks, setShelovestheePicks] = useState({});
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [seasonLeaderboard, setSeasonLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('picks');
  const [countdown, setCountdown] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [lastKnownStreak, setLastKnownStreak] = useState(0);
  
  // Track streak changes and trigger celebrations
  useEffect(() => {
    if (myStats?.current_streak >= 3 && myStats.current_streak > lastKnownStreak) {
      // User hit a new streak milestone!
      setShowStreakCelebration(true);
      triggerCelebration(myStats.current_streak);
      
      // Show toast based on streak level
      if (myStats.current_streak >= 10) {
        toast.success('🏆 LEGENDARY! 10+ correct picks in a row!', { duration: 5000 });
      } else if (myStats.current_streak >= 5) {
        toast.success('🔥 ON FIRE! 5 correct picks in a row!', { duration: 4000 });
      } else if (myStats.current_streak >= 3) {
        toast.success('⚡ HOT STREAK! 3 correct picks in a row!', { duration: 3000 });
      }
      
      setTimeout(() => setShowStreakCelebration(false), 3000);
    }
    if (myStats?.current_streak !== undefined) {
      setLastKnownStreak(myStats.current_streak);
    }
  }, [myStats?.current_streak, lastKnownStreak]);
  
  // Load week data
  const loadWeekData = useCallback(async (weekId) => {
    try {
      setLoading(true);
      // Clear previous week's data so a failed load never shows stale matchups
      setGames([]);
      setShelovestheePicks({});
      setUserPicks({});
      
      // Get games for selected week
      const gamesRes = await axios.get(`${API}/pickem/games`, {
        params: { week_id: weekId }
      });
      setGames(gamesRes.data);
      
      // Get SheLovesThee picks
      const shelovestheeRes = await axios.get(`${API}/pickem/shelovesthee-picks`, {
        params: { week_id: weekId }
      });
      setShelovestheePicks(shelovestheeRes.data);
      
      // Get weekly leaderboard
      try {
        const weeklyLbRes = await axios.get(`${API}/pickem/leaderboard/weekly`, {
          params: { week_id: weekId }
        });
        setWeeklyLeaderboard(weeklyLbRes.data);
      } catch (e) {
        console.error('Failed to load weekly leaderboard:', e);
      }
      
      // Get season leaderboard
      try {
        const seasonLbRes = await axios.get(`${API}/pickem/leaderboard/season`);
        setSeasonLeaderboard(seasonLbRes.data);
      } catch (e) {
        console.error('Failed to load season leaderboard:', e);
      }
      
      // Get user picks if logged in
      if (user) {
        const picksRes = await axios.get(`${API}/pickem/picks/${user.id}`, {
          params: { week_id: weekId }
        });
        const picksMap = {};
        picksRes.data.forEach(p => { picksMap[p.game_id] = p; });
        setUserPicks(picksMap);
        
        // Get my stats
        const statsRes = await axios.get(`${API}/pickem/stats/${user.id}`);
        setMyStats(statsRes.data);
      }
      
      // Calculate countdown
      setCountdown(getCountdownToFirstGame(gamesRes.data));
      
    } catch (error) {
      console.error('Failed to load week data:', error);
      toast.error("Couldn't load this week's matchups. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Initial load - get all weeks and current week
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Get all weeks
        const weeksRes = await axios.get(`${API}/pickem/weeks`);
        setAllWeeks(weeksRes.data);
        
        // Get current week
        const weekRes = await axios.get(`${API}/pickem/weeks/current`);
        setCurrentWeek(weekRes.data);
        setSelectedWeekId(weekRes.data.id);
        
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    loadInitialData();
  }, []);
  
  // Load week data when selected week changes
  useEffect(() => {
    if (selectedWeekId) {
      loadWeekData(selectedWeekId);
    }
  }, [selectedWeekId, loadWeekData]);
  
  // Handle week change from dropdown
  const handleWeekChange = (weekId) => {
    setSelectedWeekId(weekId);
    setPendingPicks({}); // Clear pending picks when switching weeks
  };
  
  // Refresh function for admin panel
  const handleAdminRefresh = async () => {
    // Reload weeks
    const weeksRes = await axios.get(`${API}/pickem/weeks`);
    setAllWeeks(weeksRes.data);
    
    // Reload current week
    const weekRes = await axios.get(`${API}/pickem/weeks/current`);
    setCurrentWeek(weekRes.data);
    setSelectedWeekId(weekRes.data.id);
    
    // Reload week data
    loadWeekData(weekRes.data.id);
  };
  
  // Update countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdownToFirstGame(games));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [games]);
  
  // Handle selecting a pick (doesn't save yet - just marks as pending)
  const handleSelectPick = useCallback((gameId, teamId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    // Set as pending pick
    setPendingPicks(prev => ({
      ...prev,
      [gameId]: teamId
    }));
  }, [user]);
  
  // Save all pending picks to the server
  const saveAllPicks = useCallback(async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    
    const pendingGames = Object.keys(pendingPicks);
    if (pendingGames.length === 0) {
      toast.info('No new picks to save');
      return;
    }
    
    setSaving(true);
    const userId = user.user_id || user.id;
    let savedCount = 0;
    let errors = [];
    
    for (const gameId of pendingGames) {
      const teamId = pendingPicks[gameId];
      // Skip if already saved with same pick
      if (userPicks[gameId]?.selected_team_id === teamId) continue;
      
      try {
        await axios.post(`${API}/pickem/picks`, 
          { game_id: gameId, selected_team_id: teamId },
          { params: { user_id: userId } }
        );
        
        // Update saved picks
        setUserPicks(prev => ({
          ...prev,
          [gameId]: { game_id: gameId, selected_team_id: teamId }
        }));
        
        savedCount++;
        
        // Get consensus for this game
        try {
          const consRes = await axios.get(`${API}/pickem/consensus/${gameId}`, {
            params: { user_id: userId }
          });
          setConsensus(prev => ({ ...prev, [gameId]: consRes.data }));
        } catch (e) {
          // Ignore consensus errors
        }
        
      } catch (error) {
        const game = games.find(g => g.id === gameId);
        errors.push(game?.away_team?.abbreviation + ' @ ' + game?.home_team?.abbreviation);
      }
    }
    
    // Clear pending picks that were saved
    setPendingPicks({});
    setSaving(false);
    
    if (savedCount > 0) {
      toast.success(`🏈 ${savedCount} pick${savedCount > 1 ? 's' : ''} saved!`);
    }
    if (errors.length > 0) {
      toast.error(`Failed to save: ${errors.join(', ')}`);
    }
  }, [user, pendingPicks, userPicks, games]);
  
  const picksCount = Object.keys(userPicks).length;
  const pendingCount = Object.keys(pendingPicks).filter(
    gameId => pendingPicks[gameId] !== userPicks[gameId]?.selected_team_id
  ).length;
  const totalGames = games.length;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading SWAC Pick&apos;em...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100" data-testid="swac-pickem-page">
      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
            <div className="text-center mb-6">
              <Trophy className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
              <h2 className="text-2xl font-bold text-slate-800">Sign In to Play</h2>
              <p className="text-slate-500">Log in to make your picks and compete on THEE leaderboard!</p>
            </div>
            <SocialLoginButtons />
            <p className="text-xs text-slate-400 text-center mt-4">
              Membership is 100% FREE
            </p>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#00204E] via-[#003366] to-[#00204E] py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4" data-testid="pickem-hero-title">
            SheLovesThee SWAC Pick&apos;em 🏈
          </h1>
          <p className="text-xl text-blue-200 mb-2">Think you know SWAC football? Prove it.</p>
          <p className="text-slate-300 mb-6">
            Pick the winners every week, earn points, climb THEE leaderboard, and see if you can beat SheLovesThee.
          </p>
          
          {/* Week Selector */}
          {allWeeks.length > 0 && (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
              <Calendar className="h-5 w-5" />
              <select
                value={selectedWeekId || ''}
                onChange={(e) => handleWeekChange(e.target.value)}
                className="bg-transparent text-white font-bold border-none focus:outline-none cursor-pointer appearance-none pr-6"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'white\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '20px' }}
                data-testid="week-selector"
              >
                {allWeeks.map(week => {
                  const weekLabel = `Week ${week.week_number}${week.id === currentWeek?.id ? ' (Current)' : week.is_open ? '' : ' (Closed)'}`;
                  return <option key={week.id} value={week.id} className="text-black" label={weekLabel} />;
                })}
              </select>
            </div>
          )}
          
          {/* Countdown */}
          {countdown && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 inline-block mb-6">
              <p className="text-white/80 text-sm mb-2">First game locks in</p>
              <div className="flex items-center gap-4 text-white">
                <div className="text-center">
                  <span className="text-3xl font-bold">{countdown.days}</span>
                  <p className="text-xs text-white/60">DAYS</p>
                </div>
                <span className="text-2xl">:</span>
                <div className="text-center">
                  <span className="text-3xl font-bold">{String(countdown.hours).padStart(2, '0')}</span>
                  <p className="text-xs text-white/60">HRS</p>
                </div>
                <span className="text-2xl">:</span>
                <div className="text-center">
                  <span className="text-3xl font-bold">{String(countdown.minutes).padStart(2, '0')}</span>
                  <p className="text-xs text-white/60">MIN</p>
                </div>
              </div>
            </div>
          )}
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setActiveTab('picks')}
              className="bg-white text-[#00204E] hover:bg-slate-100 px-8 py-3 font-bold uppercase"
              data-testid="make-picks-btn"
            >
              Make THEE Picks
            </Button>
            <Button 
              onClick={() => setActiveTab('leaderboard')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-3 font-bold uppercase"
              data-testid="view-leaderboard-btn"
            >
              View Leaderboard
            </Button>
          </div>
          
          {/* User Welcome */}
          {user ? (
            <div className="mt-6 text-white">
              <p className="text-lg">Welcome back, <span className="font-bold">{user.nickname || user.name}</span> 👋</p>
              <p className="text-sm text-white/60">
                {picksCount} of {totalGames} picks made
              </p>
            </div>
          ) : (
            <div className="mt-6">
              <button 
                onClick={() => setShowLoginModal(true)}
                className="text-white underline hover:text-blue-200 cursor-pointer bg-transparent border-none"
              >
                Log in to make your picks →
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="flex gap-1">
            {[
              { id: 'picks', label: 'Make THEE Picks', icon: Check },
              { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
              { id: 'my-picks', label: 'My Picks', icon: User },
              { id: 'how-it-works', label: 'How It Works', icon: BarChart2 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-colors ${
                  activeTab === tab.id 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Picks Tab */}
        {activeTab === 'picks' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-800">
                Week {allWeeks.find(w => w.id === selectedWeekId)?.week_number ?? currentWeek?.week_number} Matchups
              </h2>
              {user && (picksCount > 0 || pendingCount > 0) && (
                <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                  <Check className="h-5 w-5" />
                  <span className="font-bold">{picksCount}/{totalGames} Picks Saved</span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {games.map(game => (
                <MatchupCard
                  key={game.id}
                  game={game}
                  userPick={userPicks[game.id]}
                  pendingPick={pendingPicks[game.id]}
                  onSelectPick={handleSelectPick}
                  consensus={consensus[game.id]}
                  shelovestheePick={shelovestheePicks[game.id]}
                  isLoggedIn={!!user}
                />
              ))}
            </div>
            
            {/* Save Picks Button - Shows when there are pending picks */}
            {user && pendingCount > 0 && (
              <div className="mt-8 sticky bottom-4 z-20">
                <div className="bg-[#00204E] rounded-xl p-4 shadow-2xl max-w-md mx-auto">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-white">
                      <p className="font-bold">{pendingCount} unsaved pick{pendingCount > 1 ? 's' : ''}</p>
                      <p className="text-sm text-white/70">Click to lock in your picks</p>
                    </div>
                    <Button
                      onClick={saveAllPicks}
                      disabled={saving}
                      className="bg-yellow-400 hover:bg-yellow-500 text-[#00204E] font-bold px-6 py-3 rounded-lg shadow-lg"
                      data-testid="save-picks-btn"
                    >
                      {saving ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span> Saving...
                        </>
                      ) : (
                        <>
                          🔒 LOCK IN PICKS
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Message when no picks */}
            {!user && (
              <div className="mt-8 text-center">
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="bg-[#00204E] hover:bg-[#003366] text-white font-bold px-8 py-4 rounded-full"
                >
                  Sign In to Make Your Picks
                </button>
              </div>
            )}
            
            {/* All picks saved message */}
            {user && picksCount === totalGames && pendingCount === 0 && (
              <div className="mt-8 text-center bg-green-50 rounded-xl p-6">
                <div className="text-4xl mb-2">🏈🔒</div>
                <h3 className="text-xl font-bold text-green-700">ALL PICKS ARE IN!</h3>
                <p className="text-green-600">You&apos;re officially in THEE SWAC Pick&apos;em.</p>
                <p className="text-sm text-slate-500 mt-2">You can still change picks until each game kicks off.</p>
              </div>
            )}
            
            {/* Partial picks message */}
            {user && picksCount > 0 && picksCount < totalGames && pendingCount === 0 && (
              <div className="mt-8 text-center">
                <p className="text-slate-500 mb-2">You have {totalGames - picksCount} more picks to make</p>
              </div>
            )}
          </div>
        )}
        
        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5" /> This Week
              </h2>
              <Leaderboard 
                data={weeklyLeaderboard} 
                currentUserId={user?.user_id} 
                title="WEEKLY STANDINGS"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5" /> Season
              </h2>
              <Leaderboard 
                data={seasonLeaderboard} 
                currentUserId={user?.user_id}
                title="🏆 THEE LEADERBOARD"
              />
            </div>
          </div>
        )}
        
        {/* My Picks Tab */}
        {activeTab === 'my-picks' && (
          <div>
            {!user ? (
              <div className="text-center py-12">
                <User className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">Log in to see your picks</h3>
                <Button className="mt-4" onClick={() => setShowLoginModal(true)}>Log In</Button>
              </div>
            ) : (
              <div>
                {/* Streak Badge Celebration - Shows when user has 3+ streak */}
                {myStats?.current_streak >= 3 && (
                  <div className="flex justify-center mb-6" data-testid="streak-badge-container">
                    <StreakBadge streak={myStats.current_streak} showCelebration={showStreakCelebration} />
                  </div>
                )}
                
                {/* Stats Cards */}
                {myStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-xl p-4 shadow" data-testid="stat-correct-picks">
                      <div className="text-3xl font-bold text-blue-600">{myStats.correct_picks}</div>
                      <div className="text-sm text-slate-500">Correct Picks</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow" data-testid="stat-win-rate">
                      <div className="text-3xl font-bold text-slate-800">{myStats.win_percentage}%</div>
                      <div className="text-sm text-slate-500">Win Rate</div>
                    </div>
                    <div className={`rounded-xl p-4 shadow transition-all duration-300 ${myStats.current_streak >= 3 ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' : 'bg-white'}`} data-testid="stat-current-streak">
                      <div className={`text-3xl font-bold ${myStats.current_streak >= 3 ? 'text-white' : 'text-green-600'}`}>
                        {myStats.current_streak >= 3 && <span className="mr-1">{myStats.current_streak >= 10 ? '🏆' : myStats.current_streak >= 5 ? '🔥' : '⚡'}</span>}
                        {myStats.current_streak}
                      </div>
                      <div className={`text-sm ${myStats.current_streak >= 3 ? 'text-purple-100' : 'text-slate-500'}`}>Current Streak</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow" data-testid="stat-season-rank">
                      <div className="text-3xl font-bold text-purple-600">#{myStats.season_rank || '-'}</div>
                      <div className="text-sm text-slate-500">Season Rank</div>
                    </div>
                  </div>
                )}
                
                {/* Badges */}
                {myStats?.badges?.length > 0 && (
                  <div className="bg-white rounded-xl p-4 shadow mb-8">
                    <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5" /> Your Badges
                    </h3>
                    <div className="flex gap-4">
                      {myStats.badges.includes('hot_streak') && (
                        <div className="text-center">
                          <span className="text-3xl">🔥</span>
                          <p className="text-xs text-slate-500">Hot Streak</p>
                        </div>
                      )}
                      {myStats.badges.includes('10_wins') && (
                        <div className="text-center">
                          <span className="text-3xl">💯</span>
                          <p className="text-xs text-slate-500">10 Wins</p>
                        </div>
                      )}
                      {myStats.badges.includes('perfect_week') && (
                        <div className="text-center">
                          <span className="text-3xl">🎯</span>
                          <p className="text-xs text-slate-500">Perfect Week</p>
                        </div>
                      )}
                      {myStats.badges.includes('weekly_champion') && (
                        <div className="text-center">
                          <span className="text-3xl">🏆</span>
                          <p className="text-xs text-slate-500">Weekly Champ</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* View Full History Link */}
                <div className="flex justify-center mb-6">
                  <a 
                    href="/pick-history"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-lg transition-all shadow-lg"
                    data-testid="view-pick-history-btn"
                  >
                    📋 View Full Pick History
                  </a>
                </div>
                
                {/* Current Week Picks */}
                <h3 className="font-bold text-slate-800 mb-4">
                  Week {allWeeks.find(w => w.id === selectedWeekId)?.week_number ?? currentWeek?.week_number} Picks
                </h3>
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  {games.map(game => {
                    const pick = userPicks[game.id];
                    const picked = pick?.selected_team_id === game.away_team?.id ? game.away_team : 
                                   pick?.selected_team_id === game.home_team?.id ? game.home_team : null;
                    
                    return (
                      <div key={game.id} className="flex items-center justify-between p-4 border-b border-slate-100 last:border-b-0">
                        <div>
                          <p className="font-semibold text-slate-800">
                            {game.away_team?.abbreviation} @ {game.home_team?.abbreviation}
                          </p>
                          <p className="text-xs text-slate-500">{formatKickoffTime(game.kickoff_time)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {game.is_locked && <Lock className="h-4 w-4 text-red-500" />}
                          {picked ? (
                            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                              {picked.abbreviation}
                            </span>
                          ) : game.is_locked ? (
                            <span className="text-slate-400 text-sm">Missed</span>
                          ) : (
                            <span className="text-orange-500 text-sm">Not picked</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* How It Works Tab */}
        {activeTab === 'how-it-works' && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">How It Works</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow">
                <div className="bg-blue-100 rounded-full p-3">
                  <span className="text-2xl">🏈</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">1. Pick the winners</h3>
                  <p className="text-slate-600">Choose who you think will win each matchup.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow">
                <div className="bg-red-100 rounded-full p-3">
                  <span className="text-2xl">🔒</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">2. Lock it in</h3>
                  <p className="text-slate-600">Games individually lock at kickoff. You can change picks until then.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow">
                <div className="bg-green-100 rounded-full p-3">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">3. Earn points</h3>
                  <p className="text-slate-600">Every correct pick earns one point.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow">
                <div className="bg-yellow-100 rounded-full p-3">
                  <span className="text-2xl">🏆</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">4. Climb THEE leaderboard</h3>
                  <p className="text-slate-600">Compete with the SheLovesThee community all season.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 shadow">
                <div className="bg-purple-100 rounded-full p-3">
                  <span className="text-2xl">👑</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">5. Beat SheLovesThee</h3>
                  <p className="text-slate-600">Compare your picks against the official SheLovesThee selections.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-500">
                SheLovesThee SWAC Pick&apos;em is a free fan prediction game. No purchase is necessary and no wagering or gambling is involved.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Admin Panel - floating button in corner */}
      <AdminPanel onRefresh={handleAdminRefresh} />
    </div>
  );
};

export default SWACPickemPage;
